import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'

import Group from 'components/utils/Group'
import Pane from 'components/Common/Pane'
import { mediaQueryUp } from 'components/utils/breakpoint'
import useCharterFlightPromotionAvailableSeats from 'hooks/Flights/useCharterFlightPromotionAvailableSeats'
import TextButton from 'LuxKit/Button/TextButton'
import Label from 'LuxKit/Label/Label'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'

const SaleCard = styled(Pane)`
  width: ${rem(464)};
`

const SaleCardContent = styled(Group)`
  gap: ${rem(8)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(12)};
  }
`

const HeadingGroup = styled(Group)`
  gap: ${rem(2)};

  ${mediaQueryUp.tablet} {
    gap: ${rem(6)};
  }
`

interface Props {
  className?: string
  onViewSaleDates: () => void
}

function MaldivesDirectHundredDollarSaleHeroCard({ className, onViewSaleDates }: Props) {
  const intl = useIntl()
  const { availableSeats } = useCharterFlightPromotionAvailableSeats()

  return (
    <SaleCard className={className} borderRadius="M" padding="16 16" tabletPadding="24 20">
      <SaleCardContent direction="vertical" horizontalAlign="center">
        <Label
          kind="primary"
          variant="urgency"
          startIcon={<SolidFireIcon />}
        >
          {intl.formatMessage({
            id: 'sW8RHHlJM1QGkzhbQFQd',
            defaultMessage: 'Limited Time Sale',
            description: 'Urgency label for the limited-time Maldives charter flight promotion modal',
          })}
        </Label>

        {availableSeats > 0 && <HeadingGroup direction="vertical" horizontalAlign="center">
          <Heading variant="heading2" align="center">
            <FormattedMessage
              id="oLSDHFvdFKTIpL6m5o-W"
              defaultMessage="<em>$100 economy return</em>"
              description="Hero sale card heading for the Maldives charter one hundred dollar return flight promotion"
              values={{
                em: (chunks) => <em>{chunks}</em>,
              }}
            />
          </Heading>

          <Heading variant="heading6" align="center" colour="urgency">
            {intl.formatMessage(
              {
                id: 'Cvp3K05jYmL3W4wPq7Wk',
                defaultMessage: 'Hurry, only {count, plural, one {# seat} other {# seats}} available!',
                description: 'Urgency heading for the Maldives charter sale card showing limited promotional seat availability',
              },
              { count: availableSeats },
            )}
          </Heading>
        </HeadingGroup> }

        {!availableSeats && <HeadingGroup direction="vertical" horizontalAlign="center">
          <Heading variant="heading4" align="center">
            <FormattedMessage
              id="jMwY6GkWF6ZIQD33xXfK"
              defaultMessage="100 economy seats at"
              description="Hero sale card heading for the Maldives charter one hundred dollar return flight promotion"
            />
          </Heading>

          <Heading variant="heading2" align="center">
            <FormattedMessage
              id="n3P0tqbxmTsOIrji460s"
              defaultMessage="<em>$100 return</em>"
              description="Hero sale card heading for the Maldives charter one hundred dollar return flight promotion"
              values={{
                em: (chunks) => <em>{chunks}</em>,
              }}
            />
          </Heading>
        </HeadingGroup> }

        <BodyText variant="medium" align="center" colour="neutral-two">
          {intl.formatMessage({
            id: 'Zi6CFGqPMIuSa79YLPgF',
            defaultMessage: 'Available as part of our bundled hotel stays. On select dates only.',
            description: 'Description text for the Maldives charter sale card explaining package eligibility',
          })}
        </BodyText>

        <TextButton
          kind="primary"
          size="small"
          onClick={onViewSaleDates}
        >
          {intl.formatMessage({
            id: 'T0d9HZSgViU8syB7slXt',
            defaultMessage: 'View sale dates',
            description: 'Button text on the Maldives charter sale card opening the sale date picker',
          })}
        </TextButton>
      </SaleCardContent>
    </SaleCard>
  )
}

export default MaldivesDirectHundredDollarSaleHeroCard
